import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { KTSVG } from 'src/_metronic/helpers';
import useModal from 'src/components/modal/useModal';

import { BUSINESS_LANGUAGES } from '../../../../common/constants';
import LanguageSelector from '../../../../components/languageSelector/LanguageSelector';
import { useUploadStoreFilesMutation, useUploadStoreImageMutation } from '../../../../reducers/stores/storeApiSlice';

const TabFiles = ({ setValue, watch, defaultLocale = BUSINESS_LANGUAGES[0].code }) => {
	const { ref } = useModal();
	const [fileLang, setFileLanguage] = useState(defaultLocale);
	const { REACT_APP_API_URL: API_ROOT } = process.env;
	const [uploadImage] = useUploadStoreImageMutation([]);
	const [uploadFile] = useUploadStoreFilesMutation([]);
	const [uploadingInputId, setUploadingInputId] = useState(null);
	useEffect(() => {
		if (defaultLocale) {
			setFileLanguage((prev) => (prev === defaultLocale ? prev : defaultLocale));
		}
	}, [defaultLocale]);

	const menuFile = watch(`translations[${fileLang}].menu` ?? 'menu');
	const bannerFile = watch('banner');
	const mediaFiles = watch('media');
	const getFileName = (file) => {
		if (file.title) {
			return file.title;
		}
		if (file.name) {
			return file.name;
		}
		if (file.url) {
			return file.url;
		}
		return 'Unnamed File';
	};

	const resolveLocale = (item) => {
		if (!item || typeof item !== 'object') {
			return defaultLocale;
		}

		return item.locale ?? defaultLocale;
	};

	const updateMenuState = (file, locale) => {
		const currentMenu = watch('menu') ?? [];
		const sanitizedMenu = currentMenu.filter((item) => resolveLocale(item) !== locale);

		setValue('menu', [...sanitizedMenu, { ...file, locale }], { shouldDirty: true });
	};

	const handleUploadFile = async (e) => {
		const locale = fileLang;
		const input = e?.target;
		const registry = e?.target.id;
		const files = input.files;
		const isMultiple = input.multiple;
		const isMenuField = typeof registry === 'string' && registry.includes('translations[') && registry.endsWith('].menu');

		if (!files || files.length === 0) {
			return;
		}

		// Set uploading state for this input
		setUploadingInputId(registry);

		try {
			// Get existing files if multiple input
			const existingFiles = isMultiple ? watch(registry) ?? [] : [];

			// Process all files if multiple, otherwise just the first one
			const filesToProcess = isMultiple ? Array.from(files) : [files[0]];
			const uploadedFiles = [];

			for (const file of filesToProcess) {
				const formData = new FormData();
				const type = file.type;

				if (type === 'image/jpg' || type === 'image/jpeg' || type === 'image/png') {
					formData.append('image', file);
					if (isMenuField) {
						formData.append('locale', locale);
					}
					const response = await uploadImage(formData);
					const { data } = response ?? {};

					if (!data) {
						continue;
					}

					const { id, media } = data;
					const baseFileData = { _id: id, title: media?.title ?? file?.name, type: 'image', url: media?.url ?? '' };
					const fileData = isMenuField ? { ...baseFileData, locale } : baseFileData;

					uploadedFiles.push(fileData);
				} else if (type === 'application/pdf') {
					formData.append('file', file);
					// formData.append('private_file', true);
					if (isMenuField) {
						formData.append('locale', locale);
					}
					const response = await uploadFile(formData);
					const { data } = response ?? {};

					if (!data) {
						continue;
					}

					const { id, media } = data;
					const baseFileData = { _id: id, title: media?.title ?? file?.name, type: 'file', url: media?.url ?? '' };
					const fileData = isMenuField ? { ...baseFileData, locale } : baseFileData;

					uploadedFiles.push(fileData);
				}
			}

			if (uploadedFiles.length === 0) {
				return null;
			}

			// If multiple, append to existing files, otherwise replace
			const newFiles = isMultiple ? [...existingFiles, ...uploadedFiles] : uploadedFiles;
			setValue(registry, newFiles, { shouldDirty: true });

			if (isMenuField && uploadedFiles.length > 0) {
				uploadedFiles.forEach((fileData) => {
					updateMenuState(fileData, locale);
				});
			}

			return uploadedFiles;
		} finally {
			// Clear uploading state
			setUploadingInputId(null);
		}
	};

	const handleLanguageChange = (language) => setFileLanguage(language);
	const handleDeletion = (file, language) => {
		setValue(file, [], { shouldDirty: true });

		if (language) {
			const currentMenu = watch('menu') ?? [];
			const updatedMenu = currentMenu.filter((item) => resolveLocale(item) !== language);

			setValue('menu', updatedMenu, { shouldDirty: true });
		}
	};

	const handleMediaDeletion = (fileId) => {
		const currentMedia = watch('media') ?? [];
		const updatedMedia = currentMedia.filter((file) => file._id !== fileId);
		setValue('media', updatedMedia, { shouldDirty: true });
	};

	return (
		<div className="modal-content">
			<form data-np-autofill-type="identity" data-np-checked="1" data-np-watching="1" ref={ref}>
				<div className="row pb-5">
					<div className="col-12">
						<div className="mb-0 fs-2 fw-bold">
							<FormattedMessage id="LOCATION.FILES_TAB.MENU_FILES" />
						</div>
					</div>
				</div>
				<div className="p-3 generic-border">
					<LanguageSelector language={fileLang} onLanguageChange={handleLanguageChange} />
					<div className="row mt-5">
						<div className="col-12">
							<div className="d-flex align-items-center">
								<div className="uploader-container fs-5 fw-bold flex-grow-1">
									<div className="uploader-wrapper p-4">
										<input
											type="file"
											id={`translations[${fileLang}].menu`}
											className="custom-checkbox w-100 h-100"
											onChange={handleUploadFile}
											accept="application/pdf, image/jpg, image/jpeg, image/png"
										/>
										<label htmlFor={`translations[${fileLang}].menu`} className="uploader">
											{uploadingInputId === `translations[${fileLang}].menu` ? (
												<div className="mb-4 mt-1 rotating">
													<svg xmlns="http://www.w3.org/2000/svg" width="52" height="34" viewBox="0 0 33 34" fill="none">
														<path
															opacity="0.1"
															d="M32 17C32 25.8366 24.8366 33 16 33C7.16344 33 0 25.8366 0 17C0 8.16344 7.16344 1 16 1C24.8366 1 32 8.16344 32 17ZM5.2478 17C5.2478 22.9383 10.0617 27.7522 16 27.7522C21.9383 27.7522 26.7522 22.9383 26.7522 17C26.7522 11.0617 21.9383 6.2478 16 6.2478C10.0617 6.2478 5.2478 11.0617 5.2478 17Z"
															fill="#40798C"
														/>
														<path
															d="M16 3.70736C16 2.21213 14.7794 0.976698 13.3055 1.22851C11.3329 1.56553 9.43286 2.27106 7.70771 3.31651C5.2072 4.83184 3.16971 7.00338 1.81657 9.59525C0.463428 12.1871 -0.153628 15.1002 0.0324272 18.0181C0.218483 20.9361 1.20054 23.7472 2.87191 26.1462C4.54329 28.5452 6.84008 30.4404 9.51281 31.6259C12.1855 32.8113 15.132 33.2418 18.0322 32.8704C20.9323 32.4991 23.6753 31.3401 25.9631 29.5195C27.5415 28.2634 28.8616 26.7255 29.8612 24.9918C30.608 23.6964 29.872 22.1234 28.4694 21.6053C27.0667 21.0873 25.5349 21.8342 24.6789 23.0602C24.0966 23.894 23.3945 24.6435 22.5914 25.2826C21.0778 26.4871 19.2631 27.2539 17.3444 27.4995C15.4258 27.7452 13.4764 27.4605 11.7082 26.6762C9.93998 25.8919 8.42047 24.6381 7.31472 23.051C6.20897 21.4638 5.55927 19.604 5.43618 17.6736C5.31309 15.7432 5.72132 13.8159 6.61653 12.1012C7.51174 10.3864 8.8597 8.94979 10.514 7.94728C11.3918 7.41531 12.3381 7.01637 13.322 6.75907C14.7686 6.38079 16 5.2026 16 3.70736Z"
															fill="#40798C"
														/>
													</svg>
												</div>
											) : (
												<KTSVG className="svg-icon-4x svg-icon-primary" path="/media/icons/duotune/files/fil009.svg" />
											)}
											<div className="uploader-text">
												<span className="fs-bold">
													<FormattedMessage id="FILES.UPLOAD" />
												</span>
												{menuFile && menuFile.length > 0 ? (
													<div className="d-inline-flex align-items-center">
														<span className="fs-7 text-primary">{getFileName(menuFile[0])}</span>
													</div>
												) : (
													<span className="fs-7 text-primary">
														<FormattedMessage id="FILES.DRAG.DROP" />
													</span>
												)}
											</div>
										</label>
									</div>
								</div>

								{menuFile && menuFile.length > 0 ? (
									<div
										className="ms-3 btn btn-icon btn-light-danger"
										style={{ width: '40px', height: '40px' }}
										onClick={(e) => {
											handleDeletion(`translations[${fileLang}].menu`, fileLang);
										}}
									>
										<KTSVG className="svg-icon-2" path="/media/icons/duotune/general/gen040.svg" />
									</div>
								) : null}
							</div>
						</div>
					</div>
				</div>

				<div className="row">
					<div className="col-12">
						<div className="separator my-5" />
					</div>
				</div>

				<div className="row pb-5">
					<div className="col-12">
						<div className="mb-0 fs-2 fw-bold">
							<FormattedMessage id="LOCATION.FILES_TAB.BANNER" />
						</div>
					</div>
				</div>

				<div className="row">
					<div className="col-12">
						<div className="d-flex align-items-center">
							<div className="uploader-container fs-5 fw-bold flex-grow-1">
								<div className="uploader-wrapper p-4">
									<input
										type="file"
										id={`banner`}
										className="custom-checkbox w-100 h-100"
										onChange={handleUploadFile}
										accept="application/pdf, image/jpg, image/jpeg, image/png"
									/>
									<label htmlFor={`banner`} className="uploader">
										{uploadingInputId === `banner` ? (
											<div className="mb-4 mt-1 rotating">
												<svg xmlns="http://www.w3.org/2000/svg" width="52" height="34" viewBox="0 0 33 34" fill="none">
													<path
														opacity="0.1"
														d="M32 17C32 25.8366 24.8366 33 16 33C7.16344 33 0 25.8366 0 17C0 8.16344 7.16344 1 16 1C24.8366 1 32 8.16344 32 17ZM5.2478 17C5.2478 22.9383 10.0617 27.7522 16 27.7522C21.9383 27.7522 26.7522 22.9383 26.7522 17C26.7522 11.0617 21.9383 6.2478 16 6.2478C10.0617 6.2478 5.2478 11.0617 5.2478 17Z"
														fill="#40798C"
													/>
													<path
														d="M16 3.70736C16 2.21213 14.7794 0.976698 13.3055 1.22851C11.3329 1.56553 9.43286 2.27106 7.70771 3.31651C5.2072 4.83184 3.16971 7.00338 1.81657 9.59525C0.463428 12.1871 -0.153628 15.1002 0.0324272 18.0181C0.218483 20.9361 1.20054 23.7472 2.87191 26.1462C4.54329 28.5452 6.84008 30.4404 9.51281 31.6259C12.1855 32.8113 15.132 33.2418 18.0322 32.8704C20.9323 32.4991 23.6753 31.3401 25.9631 29.5195C27.5415 28.2634 28.8616 26.7255 29.8612 24.9918C30.608 23.6964 29.872 22.1234 28.4694 21.6053C27.0667 21.0873 25.5349 21.8342 24.6789 23.0602C24.0966 23.894 23.3945 24.6435 22.5914 25.2826C21.0778 26.4871 19.2631 27.2539 17.3444 27.4995C15.4258 27.7452 13.4764 27.4605 11.7082 26.6762C9.93998 25.8919 8.42047 24.6381 7.31472 23.051C6.20897 21.4638 5.55927 19.604 5.43618 17.6736C5.31309 15.7432 5.72132 13.8159 6.61653 12.1012C7.51174 10.3864 8.8597 8.94979 10.514 7.94728C11.3918 7.41531 12.3381 7.01637 13.322 6.75907C14.7686 6.38079 16 5.2026 16 3.70736Z"
														fill="#40798C"
													/>
												</svg>
											</div>
										) : (
											<KTSVG className="svg-icon-4x svg-icon-primary" path="/media/icons/duotune/files/fil009.svg" />
										)}
										<div className="uploader-text">
											<span className="fs-bold">
												<FormattedMessage id="FILES.UPLOAD" />
											</span>
											{bannerFile && bannerFile.length > 0 ? (
												<div className="d-inline-flex align-items-center">
													<span className="fs-7 text-primary">{getFileName(bannerFile[0])}</span>
												</div>
											) : (
												<span className="fs-7 text-primary">
													<FormattedMessage id="FILES.DRAG.DROP" />
												</span>
											)}
										</div>
									</label>
								</div>
							</div>

							{bannerFile && bannerFile.length > 0 ? (
								<div
									className="ms-3 btn btn-icon btn-light-danger"
									style={{ width: '40px', height: '40px' }}
									onClick={(e) => {
										handleDeletion(`banner`);
									}}
								>
									<KTSVG className="svg-icon-2" path="/media/icons/duotune/general/gen040.svg" />
								</div>
							) : null}
						</div>
					</div>
				</div>

				<div className="row">
					<div className="col-12">
						<div className="separator my-5" />
					</div>
				</div>

				<div className="row pb-5">
					<div className="col-12">
						<div className="mb-0 fs-2 fw-bold">
							<FormattedMessage id="LOCATION.FILES_TAB.MEDIA_FILES" />
						</div>
					</div>
				</div>
				<div className="row">
					<div className="col-12">
						<div className="d-flex align-items-center">
							<div className="uploader-container fs-5 fw-bold flex-grow-1">
								<div className="uploader-wrapper p-4">
									<input
										type="file"
										id={`media`}
										className="custom-checkbox w-100 h-100"
										onChange={handleUploadFile}
										accept="application/pdf, image/jpg, image/jpeg, image/png"
										multiple
									/>
									<label htmlFor={`media`} className="uploader">
										{uploadingInputId === `media` ? (
											<div className="mb-4 mt-1 rotating">
												<svg xmlns="http://www.w3.org/2000/svg" width="52" height="34" viewBox="0 0 33 34" fill="none">
													<path
														opacity="0.1"
														d="M32 17C32 25.8366 24.8366 33 16 33C7.16344 33 0 25.8366 0 17C0 8.16344 7.16344 1 16 1C24.8366 1 32 8.16344 32 17ZM5.2478 17C5.2478 22.9383 10.0617 27.7522 16 27.7522C21.9383 27.7522 26.7522 22.9383 26.7522 17C26.7522 11.0617 21.9383 6.2478 16 6.2478C10.0617 6.2478 5.2478 11.0617 5.2478 17Z"
														fill="#40798C"
													/>
													<path
														d="M16 3.70736C16 2.21213 14.7794 0.976698 13.3055 1.22851C11.3329 1.56553 9.43286 2.27106 7.70771 3.31651C5.2072 4.83184 3.16971 7.00338 1.81657 9.59525C0.463428 12.1871 -0.153628 15.1002 0.0324272 18.0181C0.218483 20.9361 1.20054 23.7472 2.87191 26.1462C4.54329 28.5452 6.84008 30.4404 9.51281 31.6259C12.1855 32.8113 15.132 33.2418 18.0322 32.8704C20.9323 32.4991 23.6753 31.3401 25.9631 29.5195C27.5415 28.2634 28.8616 26.7255 29.8612 24.9918C30.608 23.6964 29.872 22.1234 28.4694 21.6053C27.0667 21.0873 25.5349 21.8342 24.6789 23.0602C24.0966 23.894 23.3945 24.6435 22.5914 25.2826C21.0778 26.4871 19.2631 27.2539 17.3444 27.4995C15.4258 27.7452 13.4764 27.4605 11.7082 26.6762C9.93998 25.8919 8.42047 24.6381 7.31472 23.051C6.20897 21.4638 5.55927 19.604 5.43618 17.6736C5.31309 15.7432 5.72132 13.8159 6.61653 12.1012C7.51174 10.3864 8.8597 8.94979 10.514 7.94728C11.3918 7.41531 12.3381 7.01637 13.322 6.75907C14.7686 6.38079 16 5.2026 16 3.70736Z"
														fill="#40798C"
													/>
												</svg>
											</div>
										) : (
											<KTSVG className="svg-icon-4x svg-icon-primary" path="/media/icons/duotune/files/fil009.svg" />
										)}
										<div className="uploader-text">
											<span className="fs-bold">
												<FormattedMessage id="FILES.UPLOAD" />
											</span>
											<span className="fs-7 text-primary">
												<FormattedMessage id="FILES.DRAG.DROP" />
											</span>
										</div>
									</label>
								</div>
							</div>
						</div>
						{mediaFiles && mediaFiles.length > 0 && (
							<div className="mt-5">
								{mediaFiles.map((file) => (
									<div key={file._id || file.id} className="d-flex align-items-center mb-3">
										<div className="p-3 bg-light rounded-3 w-100 d-inline-flex align-items-center">
											<img
												className="object-fit-cover me-5 rounded-3"
												width={50}
												height={50}
												src={API_ROOT + '/' + (file.url_sm ?? file.url)}
												alt={file.title}
											/>
											<div className="fs-7">
												<span className="fw-bold">{getFileName(file)}</span>
												<br />
												<span className="text-primary">{file.url.split('/').pop()}</span>
											</div>
										</div>
										<div
											className="ms-3 btn btn-icon btn-light-danger flex-shrink-0"
											style={{ width: '40px', height: '40px' }}
											onClick={(e) => {
												handleMediaDeletion(file._id || file.id);
											}}
										>
											<KTSVG className="svg-icon-2" path="/media/icons/duotune/general/gen040.svg" />
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</form>
		</div>
	);
};

export default TabFiles;
